import type { EvalVariantInput } from "contracts/api/evals/batches";
import type { PromptBundle } from "contracts/api/prompts/responses";
import { EVAL_VARIANT_SLOT, type EvalVariantSlot } from "contracts/enums/evals";
import { createSignal, For } from "solid-js";
import { adminGateProps } from "../../store/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { FormError } from "../../ui/FormError";
import { createPromptBundles } from "../playground/playgroundQueries";

/**
 * The two-variant launch form. `agentModel` and the prompt bundle are the
 * experiment's independent variables — analyst input recorded on the batch,
 * not tuning — so they are free text with the current baselines offered as
 * suggestions rather than a fixed list.
 */

// Suggested starting points for this eval program, offered as prefills the
// analyst is expected to change. Neither is a claim about what any other
// surface is configured to run.
const SUGGESTED_MODELS = [
  "minimax/minimax-m3",
  "deepseek/deepseek-v4-flash",
] as const;
const MODEL_DATALIST_ID = "eval-agent-models";

const SLOT_COPY: Record<EvalVariantSlot, { title: string; hint: string }> = {
  a: { title: "Variant A — control", hint: "What you are measuring against." },
  b: { title: "Variant B — candidate", hint: "The change under test." },
};

function VariantFields(props: {
  slot: EvalVariantSlot;
  value: EvalVariantInput;
  onChange: (next: EvalVariantInput) => void;
  bundles: PromptBundle[] | undefined;
}) {
  return (
    <div class="panel-body">
      <div class="panel-title">{SLOT_COPY[props.slot].title}</div>
      <p class="landing-sub">{SLOT_COPY[props.slot].hint}</p>
      <input
        class="editor-input"
        placeholder="Label"
        value={props.value.label}
        onInput={(event) =>
          props.onChange({ ...props.value, label: event.currentTarget.value })
        }
      />
      <input
        class="editor-input mono"
        list={MODEL_DATALIST_ID}
        placeholder="Agent model"
        value={props.value.agentModel}
        onInput={(event) =>
          props.onChange({
            ...props.value,
            agentModel: event.currentTarget.value,
          })
        }
      />
      <select
        class="editor-input"
        value={props.value.promptBundleId ?? ""}
        onChange={(event) =>
          props.onChange({
            ...props.value,
            promptBundleId: event.currentTarget.value || null,
          })
        }
      >
        <option value="">Active yeet-agent prompt (default)</option>
        <For each={props.bundles}>
          {(bundle) => (
            <option value={bundle.id}>
              {bundle.label}
              {bundle.isActive ? " ★" : ""}
            </option>
          )}
        </For>
      </select>
    </div>
  );
}

export function EvalBatchLaunchForm(props: {
  suiteId: string;
  onLaunched: (batchId: string) => void;
}) {
  const bundles = createPromptBundles();
  const [variantA, setVariantA] = createSignal<EvalVariantInput>({
    label: "control",
    agentModel: SUGGESTED_MODELS[0],
    promptBundleId: null,
  });
  const [variantB, setVariantB] = createSignal<EvalVariantInput>({
    label: "candidate",
    agentModel: SUGGESTED_MODELS[1],
    promptBundleId: null,
  });
  const action = createAsyncAction();

  const filled = (variant: EvalVariantInput) =>
    variant.label.trim().length > 0 && variant.agentModel.trim().length > 0;
  const ready = () => filled(variantA()) && filled(variantB());

  const launch = (event: Event) => {
    event.preventDefault();
    if (!ready() || action.busy()) return;
    void action.run(async () => {
      const batch = await api.evals.createBatch({
        suiteId: props.suiteId,
        variantA: variantA(),
        variantB: variantB(),
      });
      props.onLaunched(batch.batchId);
    });
  };

  return (
    <section class="panel">
      <div class="panel-head">
        <span class="panel-title">Launch a batch</span>
      </div>
      <form onSubmit={launch}>
        <datalist id={MODEL_DATALIST_ID}>
          <For each={SUGGESTED_MODELS}>
            {(model) => <option value={model} />}
          </For>
        </datalist>
        <VariantFields
          slot={EVAL_VARIANT_SLOT.A}
          value={variantA()}
          onChange={setVariantA}
          bundles={bundles.data?.bundles}
        />
        <VariantFields
          slot={EVAL_VARIANT_SLOT.B}
          value={variantB()}
          onChange={setVariantB}
          bundles={bundles.data?.bundles}
        />
        <div class="panel-body">
          <FormError message={action.error()} />
          <button
            class="btn btn-primary"
            type="submit"
            {...adminGateProps(action.busy() || !ready(), "launch")}
          >
            {action.busy() ? "Launching…" : "Launch batch"}
          </button>
          <p class="landing-sub">
            Every page in the suite runs both variants over one shared capture,
            then a judge compares the two outputs.
          </p>
        </div>
      </form>
    </section>
  );
}
